import { useEffect, useRef } from "react"

// Two windows/monitors that never occlude each other never fire
// visibilitychange at all — the SPA tab and the RSVP email tab can sit
// side by side for the entire time a card is open. A bare focus listener
// would defeat the feature's own motivating scenario in that layout, so
// this also polls while visible, the same backstop useNewVersionAvailable
// gives its own visibilitychange accelerant.
const DEFAULT_POLL_INTERVAL_MS = 30_000

// A browser can fire visibilitychange more than once for one real focus
// change, and alt-tabbing rapidly would otherwise queue a fresh request per
// flip; the stale-response guards each caller already needs cover
// *correctness*, this just keeps it from being chatty.
const DEFAULT_MIN_INTERVAL_MS = 2_000

interface UseRefetchOnFocusOptions {
  pollIntervalMs?: number
  minIntervalMs?: number
}

/**
 * Re-runs `callback` while `enabled`: when the tab regains focus, and as a
 * backstop, on a plain interval.
 *
 * For data a change elsewhere never reaches this app's own state to
 * invalidate — an RSVP answered from a separate, unauthenticated email link
 * is the case this exists for. This hook owns only the trigger, so the
 * caller's own fetcher decides what "refresh" means for its state (silent
 * background update vs. a full loading reset).
 */
export const useRefetchOnFocus = (
  callback: () => void,
  enabled: boolean,
  { pollIntervalMs = DEFAULT_POLL_INTERVAL_MS, minIntervalMs = DEFAULT_MIN_INTERVAL_MS }: UseRefetchOnFocusOptions = {}
): void => {
  const lastRunAtRef = useRef(0)

  // A caller's own callback is rarely memoized perfectly (both current call
  // sites recreate theirs whenever the id they're fetching changes), and the
  // effect below must not tear down and recreate the listener/interval on
  // every such change — that would restart the poll's cadence from zero
  // each time. Always-current ref instead of a dependency, so the effect
  // itself depends only on what actually needs to restart it.
  const callbackRef = useRef(callback)
  useEffect(() => {
    callbackRef.current = callback
  })

  useEffect(() => {
    if (!enabled) return

    const run = () => {
      const now = Date.now()
      if (now - lastRunAtRef.current < minIntervalMs) return
      lastRunAtRef.current = now
      callbackRef.current()
    }
    const runIfVisible = () => {
      if (document.visibilityState === "visible") run()
    }

    document.addEventListener("visibilitychange", runIfVisible)
    const timer = window.setInterval(runIfVisible, pollIntervalMs)

    return () => {
      document.removeEventListener("visibilitychange", runIfVisible)
      window.clearInterval(timer)
    }
  }, [enabled, pollIntervalMs, minIntervalMs])
}
